import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import List from '@mui/joy/List';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';

import WorkListItem from 'components/WorkListItem';
import { WorkItemType } from 'components/WorkListItem';

import getIcon from 'utils/getIcon';
import pluralize from 'utils/pluralize';
import useDedicatedAssignment from 'hooks/useDedicatedAssignment';
import useCurrentIdentity from 'hooks/useCurrentIdentity';
import useCollabs from 'hooks/useCollabs';

import { CollaboratorsProps } from '../stores/collaborators_store';
import { CollabProps } from 'stores/collab_store';

import { forageGet, forageSet } from 'utils/misc';

const WorkList = observer(({ items, paged, loading, title = null }: { items: WorkItemType[], paged: boolean, loading: boolean, title: JSX.Element | null }) => {
    const [page, setPage] = useState(1);
    const DownIcon = getIcon('ArrowDropDown');
    const PAGE_SIZE = 5;
    const { isDedicatedFor, fetchDedicatedAssignmentsByAssistantIdentity } = useDedicatedAssignment();
    const currentIdentity = useCurrentIdentity();
    const [filter, setFilter] = useState<string[]>(['dedicated', 'request', 'collab']);
    const [fetching, setFetching] = useState(false);
    const { getCollab } = useCollabs();

    const [requestCount, setRequestCount] = useState(0);
    const [collabsCount, setCollabsCount] = useState(0);
    const [dedicatedCount, setDedicatedCount] = useState(0);
    const [dedicatedCollabs, setDedicatedCollabs] = useState<CollabProps[]>([]);

    const CACHED_FILTERS = `worklist_filters:${title}`;
    useEffect(() => {
        if (!currentIdentity?.uuid) return;
        if(!currentIdentity?.dedicated) return;
        if (fetching) return;
        setFetching(true);

        fetchDedicatedAssignmentsByAssistantIdentity(currentIdentity.uuid)
            .finally(() => {
                setFetching(false)
            });
    }, [currentIdentity?.uuid]);



    useEffect(() => {
        forageGet<string[]>(CACHED_FILTERS)
            .then((data) => {
                if (data) {
                    console.log("setting filter", data);
                    setFilter(data);
                }
            });
    }, []);

    useEffect(() => {
        forageSet(CACHED_FILTERS, filter);
    }, [filter]);

    useEffect(() => {
        setRequestCount(items.filter(item => item.cls == 'request').length);
        setCollabsCount(items.filter(item => item.cls != 'request').length);
        const ded = items.filter(item => {
            const collab = _getCollab(item);
            if (collab && isDedicatedFor(currentIdentity?.uuid, collab.identity)) {
                return collab as CollabProps;
            }
            return null;
        });

        setDedicatedCollabs((ded || []) as CollabProps[]);
        setDedicatedCount(ded.length);

    }, [filter, items]);


    function _getCollab(item: WorkItemType) {
        return (item as CollaboratorsProps).collab ? getCollab((item as CollaboratorsProps).collab) : null;
    }

    const filteredItems = filter.length ? getFilteredItems() : [];
    const showing = paged ? filteredItems.slice(0, page * PAGE_SIZE) : filteredItems;

    function allItems() {
        return items.filter((m) => {
            if(m.cls == 'collaborators') {
                if(_getCollab(m)?.identity == currentIdentity?.uuid) {
                    return false;
                }
            }
            return true;
        }
        )
    }

    function getFilteredItems() {
        return allItems().filter(item => {

            // show the requests?
            if (filter.includes('request') && item.cls == 'request')
                return true;

            // show the collabs?
            if ((filter.includes('collab') || filter.includes('dedicated')) && item.cls != 'request') {

                // if they want us to hide them
                if(!filter.includes('dedicated')) {
                    if(dedicatedCollabs.find(i => i.uuid == item.uuid)) {
                        // console.log("They want us to hide them");
                        return false;
                    }
                }

                // unless we're hiding the dedicated and it is one
                if (filter.includes('dedicated')) {
                    if (dedicatedCollabs.find(i => i.uuid == item.uuid)) {
                        return true;
                    }
                }

                if(filter.includes('collab')) {
                    return true;
                }
            }

            return false;
        });
    }

    function toggleFilter(s: string) {
        if(filter.includes(s)) {
            setFilter([...filter].filter(item => item != s));
        }
        else
            setFilter([s, ...filter]);
    }


    return (
        <List>
            <Stack

                justifyContent="space-between"
                borderBottom={1}
                borderColor="divider"
                spacing={1}
                pb={1}
                m={1} >
                {title}
                <Stack>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1}
                    >

                    { currentIdentity.dedicated &&
                        <Tooltip arrow
                         title={`${!filter.includes('dedicated') ? 'Hide' : 'Show' } ${pluralize(dedicatedCount, 'task')} from your dedicated clients`}>
                            <Chip
color="primary"
                                size="sm"
                                onClick={() => toggleFilter('dedicated')}
                                variant={filter.includes('dedicated') ? 'solid' : "outlined"}
                            >{pluralize(dedicatedCount, 'Dedicated')}</Chip>
                            </Tooltip> }

                        <Tooltip arrow
                            title={`${filter.includes('request') ? 'Hide' : 'Show' } ${pluralize(requestCount, 'Standard Request')}`}>
                            <Chip
color="primary"
                                size="sm"
                                onClick={() => toggleFilter('request')}
                                variant={filter.includes('request') ? 'solid' : "outlined"}

                            >{pluralize(requestCount, 'Request')}</Chip>
                        </Tooltip>

                        <Tooltip arrow
                                      title={`${filter.includes('collab') ? 'Hide' : 'Show' } ${pluralize(collabsCount, 'collab')}`}
                        >
                        <Chip
color="primary"
                            size="sm"
                            onClick={() => toggleFilter('collab')}
                            variant={filter.includes('collab') ? 'solid' : "outlined"}

                        >{pluralize(collabsCount, 'Collab')}
                        </Chip>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Stack>

            { !showing.length && !loading &&
                <Stack
                    alignItems="center"
                    direction="row"
                    spacing={1}
                    justifyContent="center" sx={{ py: 2 }}>
                    <Typography
                        level="body-xs"
                    >Nothing to show</Typography>
                </Stack>
            }
            {showing.map((item) => {
                return <WorkListItem
                    key={item.uuid}
                    item={item} />
            })
            }

            {paged &&
                <Stack
                    alignItems="center"
                    direction="row"
                    spacing={1}
                    justifyContent="center" sx={{ py: 2 }}>

                    {page > 1 && (
                        <Chip
                            variant="outlined"
                            color="neutral"
                            onClick={() => setPage(page - 1)}
                            endDecorator={<DownIcon sx={{ transform: 'rotate(180deg)' }} />}
                            slotProps={{ root: { component: 'span' } }}
                        >Show Fewer</Chip>
                    )}

                    {items.length > page * PAGE_SIZE && (
                        <Chip
                            variant="outlined"
                            color="neutral"
                            onClick={() => setPage(page + 1)}
                            endDecorator={<DownIcon />}
                            slotProps={{ root: { component: 'span' } }}
                        >Show More</Chip>
                    )}
                </Stack>
            }

        </List>

    );
});
export default WorkList;
